import { Map } from 'ol';
import { useCallback, useContext, useEffect } from 'react';
import { Group } from 'ol/layer';
import Layer from 'ol/layer/Layer';
import disposeLayerAfterRender from 'utils/layerLifecycle';
import { Style, Stroke } from 'ol/style';
import BaseLayerContext from '../../BaseLayerContext';
import {
  createBaseLayer, createKMLLayer, getLayerByName,
} from './utils';
import { KML_ARENA_PATH, KML_CTR_PATH, KML_FIR_PATH } from '../../../configs/endpoints';
import LayerName from './enums';
import zIndexes from '../../../configs/zIndexes';

const FIR_STYLE = new Style({ stroke: new Stroke({ color: '#0000ff', width: 2 }) });

function useBaseLayers(map: Map) {
  const { selectedBaseLayers } = useContext(BaseLayerContext);

  const updateBaseLayers = useCallback(() => {
    const baseMapsGroup = map?.getLayers()?.getArray()?.find(
      (l) => l.get('name') === LayerName.BaseMapsLayer,
    );
    selectedBaseLayers.forEach((layer) => {
      if (!getLayerByName(map, layer.layerName) && !(baseMapsGroup as Group)?.getLayers()?.getArray()?.find((l) => l.get('name') === layer.layerName)) {
        if (layer.layerName === LayerName.ARENALayer) {
          const kmlLayer = createKMLLayer(KML_ARENA_PATH);
          kmlLayer.set('name', LayerName.ARENALayer);
          kmlLayer.setZIndex(zIndexes.ARENALAYER);
          map.addLayer(kmlLayer);
        } else if (layer.layerName === LayerName.CTRLayer) {
          const kmlLayer = createKMLLayer(KML_CTR_PATH);
          kmlLayer.set('name', LayerName.CTRLayer);
          kmlLayer.setZIndex(zIndexes.CTR);
          map.addLayer(kmlLayer);
        } else if (layer.layerName === LayerName.FIRLayer) {
          const kmlLayer = createKMLLayer(KML_FIR_PATH, FIR_STYLE);
          kmlLayer.set('name', LayerName.FIRLayer);
          kmlLayer.setZIndex(zIndexes.FIR);
          map.addLayer(kmlLayer);
        } else {
          const baseLayer = createBaseLayer(map, layer);
          if (layer.layerName === LayerName.Coastline) {
            baseLayer.set('name', LayerName.Coastline);
            baseLayer.setZIndex(zIndexes.COASTLINE);
            map.addLayer(baseLayer);
          } else {
            baseLayer.set('name', layer.layerName);
            baseLayer.setZIndex(zIndexes.BASELAYERS + layer.level);
            if (baseMapsGroup) {
              (baseMapsGroup as Group).getLayers().push(baseLayer);
            }
          }
        }
      }
    });

    const selectedBaseLayerNames = selectedBaseLayers.map((layer) => layer.layerName);
    const activeLayers = map.getAllLayers();
    activeLayers.forEach((layer) => {
      const currLayerName = layer.get('name');
      if (!selectedBaseLayerNames.includes(currLayerName)) {
        if (currLayerName === LayerName.ARENALayer
          || currLayerName === LayerName.CTRLayer
          || currLayerName === LayerName.FIRLayer
          || currLayerName === LayerName.Coastline) {
          map.removeLayer(layer);
          disposeLayerAfterRender(map, layer as Layer);
        } else if (baseMapsGroup) {
          // Only layers that were really in the base maps group are released (products are not).
          const removed = (baseMapsGroup as Group).getLayers().remove(layer);
          if (removed) disposeLayerAfterRender(map, layer as Layer);
        }
      }
    });
  }, [map, selectedBaseLayers]);

  useEffect(() => {
    if (selectedBaseLayers && map) {
      updateBaseLayers();
    }
  }, [selectedBaseLayers, updateBaseLayers, map]);

  return {
    updateBaseLayers,
  };
}

export default useBaseLayers;
